import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquareText, Plus, Loader2, Trash2, Copy } from "lucide-react";
import { toast } from "sonner";

const CH = { email: "Email", sms: "SMS", whatsapp: "WhatsApp" };
const empty = { name: "", channel: "email", subject: "", body: "" };

export default function CommTemplates() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setRows((await api.get("/comm-templates")).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit = (t) => { setEditId(t.id); setForm({ ...empty, ...t, subject: t.subject || "" }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim() || !form.body.trim()) { toast.error("Nom et message requis"); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/comm-templates/${editId}`, form); else await api.post("/comm-templates", form);
      toast.success("Modèle enregistré"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/comm-templates/${id}`); toast.success("Supprimé"); load(); };
  const copy = (t) => { navigator.clipboard?.writeText(t.body); toast.success("Message copié"); };

  return (
    <div>
      <PageHeader title="Modèles de messages" subtitle="Modèles réutilisables (email, SMS, WhatsApp). Variables : {{client}}, {{date}}, {{entreprise}}, {{lien_avis}}" icon={MessageSquareText}
        actions={<Button onClick={openNew} data-testid="btn-new-template" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1.5" /> Nouveau modèle</Button>} />

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
        : <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{rows.map((t) => (
          <div key={t.id} data-testid={`template-row-${t.id}`} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-slate-800">{t.name}</span>
              <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-500">{CH[t.channel] || t.channel}</span>
              <div className="ml-auto flex gap-1">
                <button onClick={() => copy(t)} data-testid={`template-copy-${t.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><Copy className="w-4 h-4" /></button>
                <button onClick={() => openEdit(t)} className="text-xs text-emerald-600 px-2">Modifier</button>
                <button onClick={() => del(t.id)} data-testid={`template-del-${t.id}`} className="p-1.5 rounded hover:bg-red-500/10 text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {t.subject && <div className="text-xs text-slate-500 mb-1">Objet : {t.subject}</div>}
            <p className="text-sm text-slate-600 whitespace-pre-wrap">{t.body}</p>
          </div>
        ))}</div>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-slate-900">{editId ? "Modifier le modèle" : "Nouveau modèle"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Nom</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="template-name" className="bg-slate-50 border-slate-200" /></div>
              <div className="space-y-1.5"><Label className="text-xs text-slate-500">Canal</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}><SelectTrigger data-testid="template-channel" className="bg-slate-50 border-slate-200"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">{Object.entries(CH).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
            </div>
            {form.channel === "email" && <div className="space-y-1.5"><Label className="text-xs text-slate-500">Objet</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="bg-slate-50 border-slate-200" /></div>}
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Message</Label><Textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="template-body" className="bg-slate-50 border-slate-200" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-template" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">{saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
